import { Chip } from '@mui/material';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { payoutManualApi } from 'api';
import {
  DataGrid,
  DataGridColumnDefinition,
  dataGridColumns,
  PaginationWrapper,
} from 'components';
import { QueryKey } from 'enums';
import { usePartialQuery } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { PayoutManualBatch, PayoutManualBatchStatus } from 'types';

import { ManualBatchDetailsDialog } from './ManualBatchDetailsDialog';

export const ManualBatches: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.manual.batches',
  });

  const [batchId, setBatchId] = useState<string | null>(null);

  const queryResult = usePartialQuery(
    QueryKey.PayoutManualBatches,
    payoutManualApi.getBatchesPaginated,
    { useCachedCount: true },
  );

  const handleClose = useCallback(() => setBatchId(null), []);

  const handleRowClick = useCallback(
    (item?: PayoutManualBatch) => setBatchId(item?.id ?? null),
    [],
  );

  const columns: DataGridColumnDefinition<PayoutManualBatch>[] = useMemo(
    () => [
      dataGridColumns.getIdColumn<PayoutManualBatch>(),
      {
        header: t('columns.operation'),
        valueGetter: (item) => (
          <Fragment>
            <div>{t(`operations.${item.operation}` as any)}</div>
            {item.status === PayoutManualBatchStatus.InProgress && (
              <Chip
                size="small"
                variant="outlined"
                color="info"
                label={t('statuses.in_progress')}
              />
            )}
            {item.status === PayoutManualBatchStatus.Error && (
              <Chip
                size="small"
                variant="outlined"
                color="error"
                label={t('statuses.error')}
              />
            )}
          </Fragment>
        ),
      },
      {
        header: t('columns.operator'),
        valueGetter: (item) => item.user?.name || '—',
      },
      {
        header: t('columns.provider'),
        valueGetter: (item) => item.p2pProvider?.name || '—',
      },
      {
        header: t('columns.counts'),
        valueGetter: (item) => (
          <div className="tw-flex tw-flex-wrap tw-gap-1">
            <Chip
              size="small"
              variant="outlined"
              label={t('total', { count: item.totalCount })}
            />
            <Chip
              size="small"
              variant="outlined"
              color="success"
              label={t('succeeded', { count: item.successCount })}
            />
            {!!item.failedCount && (
              <Chip
                size="small"
                variant="outlined"
                color="error"
                label={t('failed', { count: item.failedCount })}
              />
            )}
            {!!item.disputeCount && (
              <Chip
                size="small"
                variant="outlined"
                color="warning"
                label={t('disputed', { count: item.disputeCount })}
              />
            )}
            {!!item.skippedCount && (
              <Chip
                size="small"
                variant="outlined"
                label={t('skipped', { count: item.skippedCount })}
              />
            )}
          </div>
        ),
      },
    ],
    [t],
  );

  return (
    <Fragment>
      <PaginationWrapper queryResult={queryResult}>
        <DataGrid
          columns={columns}
          data={queryResult.data?.items || []}
          onRowClick={handleRowClick}
        />
      </PaginationWrapper>
      <ManualBatchDetailsDialog batchId={batchId} onClose={handleClose} />
    </Fragment>
  );
};
